import React, { useEffect, useRef } from 'react';
import {
  Grid,
  Box,
  Typography,
  InputLabel,
  MenuItem,
  FormControl,
  FormHelperText,
} from '@mui/material';
import makeStyles from '@mui/styles/makeStyles';
import { Controller, useFormContext } from 'react-hook-form';
import { useDispatch, useSelector } from 'react-redux';
import Link from '@mui/material/Link';

import {
  checkEmptyObject,
  getPaymentConditionText,
  groupByKey,
} from '../../../../../../utils/CommonUtils';
import CardListGrid from './CardsListGrid';
import { searchPayments } from '../../slices/payment_info';
import RetryData from '../../../../../common/RetryData';
import Loading from '../../../../../common/Loading';
import { PaymentMethod } from '../../Enums';
import OutlinedSelect from '../../../../../common/OutlinedSelect';
import { renderHiddenRegister } from '../../../../../common/FormHiddenField';


const useStyles = makeStyles(() => ({
  faqLinkBox: {
    fontSize: '12px',
    color: '#a7a6a7',
    display: 'block',
    width: '100%',
    textAlign: 'right',
    paddingRight: '20px',
  },
}));

const renderFinancialInstitutionValue = (selected) => {
  if (!selected || typeof selected !== 'object') {
    return <em>Seleccione..</em>;
  }
  return selected.name ?? '';
};

const isSamePaymentCondition = (left, right) => (
  left?.paymentMethod === right?.paymentMethod
  && left?.dues === right?.dues
  && left?.monthsToDefer === right?.monthsToDefer
  && left?.applyInterest === right?.applyInterest
);

const renderPaymentConditionValue = (selected) => {
  if (!selected || typeof selected !== 'object') {
    return <em>Seleccione...</em>;
  }
  return getPaymentConditionText(selected);
};

export default function CreditPayment() {
  const classes = useStyles();
  const boxProperties = { borderRadius: 16 };
  const dispatch = useDispatch();

  const [selectedCard, setSelectedCard] = React.useState({});
  const [conditions, setConditions] = React.useState({}); // grouping by specific name
  const [paymentMethod, setPaymentMethod] = React.useState('');

  const { control, watch, setValue, formState: { errors } } = useFormContext();
  const paymentInfo = useSelector((state) => state.paymentInfo);
  const quote = useSelector((state) => state.quote);
  const { deductibleSelected = {} } = quote;
  const { data: payments } = paymentInfo;
  const [paymentConditionLabel, setPaymentConditionLabel] = React.useState('');

  const financialInstitutionWatcher = watch('creditPayment.financialInstitution');

  useEffect(() => {
    dispatch(searchPayments(deductibleSelected?.typeId === 4 ? true : undefined));
  }, [deductibleSelected, dispatch]);

  useEffect(() => {
    setSelectedCard({});
    setConditions({});
    setPaymentMethod('');
    setValue('creditPayment.paymentCondition', '');
    setValue('creditPayment.selectedCard', {});
  }, [financialInstitutionWatcher, setValue]);

  useEffect(() => {
    if (!checkEmptyObject(selectedCard)) {
      setValue('creditPayment.paymentCondition', '');
      setValue('creditPayment.selectedCard', selectedCard);
      setPaymentMethod('');
      setConditions(groupByKey(selectedCard?.conditions, 'paymentMethod'));
    }
  }, [selectedCard, setValue]);

  const onChangePaymentMethod = (event) => {
    setValue('creditPayment.paymentCondition', '');
    setPaymentMethod(event.target.value);
    setPaymentConditionLabel(event.target.value);
    if (event.target.value === PaymentMethod.Corriente) {
      // set default value only for Corriente payment method
      setValue('creditPayment.paymentCondition', conditions[event.target.value][0]);
    }
  };

  const institucionRef = useRef();
  const corrienteRef = useRef();

  if (!paymentInfo.isSuccess) {
    return (
      <Grid container spacing={2}>
        <RetryData onClickHandler={() => dispatch(searchPayments())} />
      </Grid>
    );
  }
  if (paymentInfo.loading) {
    return (
      <Loading text="Cargando Instituciones" />
    );
  }
  return (
    <Grid container spacing={2}>
      <Grid item xs={12}>
        <Box {...boxProperties}>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} sm={4}>
              <Typography component="p" color="textSecondary">
                Selecciona tu instituci&oacute;n financiera:
              </Typography>
            </Grid>
            <Grid item xs={12} sm={8}>
              <FormControl fullWidth>
                <InputLabel id="institution-select">INSTITUCIÓN FINANCIERA</InputLabel>
                <Controller
                  name="creditPayment.financialInstitution"
                  control={control}
                  defaultValue=""
                  render={({ field: { ref, value, onChange, ...fieldProps } }) => {
                    const resolvedValue = payments.find((payment) => payment.id === value?.id)
                      ?? value
                      ?? '';

                    return (
                      <OutlinedSelect
                        {...fieldProps}
                        inputRef={ref}
                        value={resolvedValue}
                        onChange={(event) => onChange(event.target.value)}
                        labelId="institution-select"
                        label="INSTITUCIÓN FINANCIERA"
                        renderValue={renderFinancialInstitutionValue}
                      >
                        <MenuItem value=""><em>Seleccione..</em></MenuItem>
                        {payments.map((payment) => (
                          <MenuItem
                            key={payment?.id}
                            value={payment}
                          >
                            {payment?.name}
                          </MenuItem>
                        ))}
                      </OutlinedSelect>
                    );
                  }}
                />
              </FormControl>
            </Grid>
          </Grid>
          <Controller
            name="creditPayment.selectedCard"
            control={control}
            defaultValue={{}}
            render={renderHiddenRegister}
          />
        </Box>
      </Grid>
      <Grid item xs={12}>
        <Box {...boxProperties}>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} sm={4}>
              <Typography component="p" color="textSecondary">
                Selecciona tu tarjeta:
              </Typography>
            </Grid>
            <Grid item xs={12} sm={8}>
              {!checkEmptyObject(financialInstitutionWatcher) && (
                <CardListGrid
                  cards={financialInstitutionWatcher?.cards}
                  setSelectedCard={setSelectedCard}
                  selectedCard={selectedCard}
                />
              )}
            </Grid>
          </Grid>
        </Box>
      </Grid>
      <Grid item xs={12}>
        <Box {...boxProperties}>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} sm={4}>
              <Typography component="p" color="textSecondary">
                Escoge tu forma de Pago:
              </Typography>
            </Grid>
            <Grid item xs={12} sm={4}>
              <FormControl fullWidth>
                <InputLabel id="payment-method-select">PAGO</InputLabel>
                <OutlinedSelect
                  labelId="payment-method-select"
                  label="PAGO"
                  value={paymentMethod}
                  onChange={onChangePaymentMethod}
                >
                  <MenuItem value=""><em>Seleccione...</em></MenuItem>
                  {Object.keys(conditions)?.map((payment, index) => (
                    <MenuItem
                      key={`payment${index}`}
                      value={payment}
                    >
                      {payment}
                    </MenuItem>
                  ))}
                </OutlinedSelect>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={4}>
              <FormControl
                fullWidth
                error={errors?.creditPayment?.hasOwnProperty('paymentCondition')}
              >
                <InputLabel id="payment-condition-select">{paymentConditionLabel}</InputLabel>
                <Controller
                  name="creditPayment.paymentCondition"
                  control={control}
                  defaultValue=""
                  render={({ field: { ref, value, onChange, ...fieldProps } }) => {
                    const conditionOptions = conditions[paymentMethod] ?? [];
                    const resolvedValue = conditionOptions.find(
                      (condition) => isSamePaymentCondition(condition, value),
                    ) ?? value ?? '';

                    return (
                      <OutlinedSelect
                        {...fieldProps}
                        ref={ref}
                        inputRef={corrienteRef}
                        value={resolvedValue}
                        onChange={(event) => onChange(event.target.value)}
                        labelId="payment-condition-select"
                        label={paymentConditionLabel}
                        renderValue={renderPaymentConditionValue}
                        onFocus={() => corrienteRef.current?.focus()}
                      >
                        <MenuItem value=""><em>Seleccione...</em></MenuItem>
                        {conditionOptions.map(
                          (condition, index) => (
                            <MenuItem
                              key={`condition${index}`}
                              value={condition}
                            >
                              {getPaymentConditionText(condition)}
                            </MenuItem>
                          ),
                        )}
                      </OutlinedSelect>
                    );
                  }}
                />
                <FormHelperText>
                  {errors.creditPayment?.paymentCondition?.message}
                </FormHelperText>
              </FormControl>
            </Grid>
          </Grid>
        </Box>
      </Grid>
      <Box className={classes.faqLinkBox}>
        Si usted tiene dudas acerca del pago con tarjeta de cr&eacute;dito,
        puede consultar la secci&oacute;n de
        <Link href="/faq" target="blank">preguntas frecuentes</Link>
      </Box>
    </Grid>
  );
}
